import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'

const resource = 'alertRules'

// Types
export type TTargetCollection = 'Resource' | 'Engagement' | 'RecruitmentProcess' | 'EmployeeOnboarding' | 'Timesheet' | 'Evaluation' | 'EvaluationCampaign' | 'TrainingSession'
export type TTriggerType = 'cron' | 'event'
export type TConditionLogic = 'and' | 'or'
export type TSeverity = 'low' | 'medium' | 'high' | 'critical'

export type TConditionOperator =
  | 'equals'
  | 'not_equals'
  | 'greater_than'
  | 'less_than'
  | 'greater_than_or_equals'
  | 'less_than_or_equals'
  | 'days_before'
  | 'days_after'
  | 'is_null'
  | 'is_not_null'
  | 'contains'
  | 'in_range'

export type TCondition = {
  field: string
  operator: TConditionOperator
  value?: string | number | boolean
  valueEnd?: string | number
}

export type TCollectionRecipientRole =
  | 'resource'
  | 'director'
  | 'manager'
  | 'managedBy'
  | 'recruiter'
  | 'authorId'
  | 'smart'
  | 'evaluators'
  | 'evaluatee'
  | 'all'
  | 'evaluator'
  | 'evaluated'
  | 'trainer'

// Recipient with populated team/resource from backend (GET response)
export type TRecipient = {
  type: 'resource' | 'team' | 'collection'
  resourceId?: string
  teamId?: string
  role?: TCollectionRecipientRole
  // Populated by backend
  team?: {
    _id: string
    display: string
    email?: string | null
  }
  resource?: {
    _id: string
    firstname: string
    lastname: string
    picture?: string | null
  }
}

// Payload for POST/PUT - only IDs, no populated objects
export type TRecipientPayload = {
  type: 'resource' | 'team' | 'collection'
  resourceId?: string
  teamId?: string
  role?: TCollectionRecipientRole
}

export type TAlertRulePayload = {
  name: string
  description?: string
  active: boolean
  targetCollection: TTargetCollection
  triggerType: TTriggerType
  conditions: TCondition[]
  conditionLogic: TConditionLogic
  recipients: TRecipientPayload[]
  officeIds: string[]
  offices?: Array<{
    _id: string
    display: string
  }>
  jobCategoryIds?: string[]
  jobCategories?: Array<{
    _id: string
    display: string
  }>
  excludeDepartureCategoryIds?: string[]
  excludeDepartureCategories?: Array<{
    _id: string
    display: string
  }>
  message: {
    subject: string
    body: string
  }
  severity: TSeverity
  cooldownHours: number
  notifyOnce?: boolean
  /** True also sends on Saturday and Sunday. Missing means weekdays only. */
  sendOnWeekends?: boolean
  /** True also sends on a public holiday of the target's office. Missing skips that day. */
  sendOnPublicHolidays?: boolean
}

export type TAlertRule = TAlertRulePayload & {
  _id: string
  recipients: TRecipient[]
  offices: Array<{
    _id: string
    display: string
  }>
}

export type TUpcomingAlert = {
  id: string
  ruleId: string
  ruleName: string
  severity: TSeverity
  plannedAt: string
  daysFromNextRun: number
  target: TAlertPreviewNotification['target']
  scheduleField: string | null
  scheduleDate: string | null
}

export type TUpcomingResponse = {
  nextRunAt: string
  days: number
  total: number
  byDay: number[]
  events: TUpcomingAlert[]
  isTruncated: boolean
  /** Daily application volume rules: checked every hour, never projected. */
  hourlyRuleCount?: number
}

export type TRunAlertEngineResponse = {
  processedRules: number
  createdAlerts: number
  alerts: Array<{
    _id: string
  }>
}

export type TAlertRulesResponse = {
  docs: TAlertRule[]
  count: {
    total: number
    currentPage: number
    totalPages: number
    hasNextPage: boolean
    limit: number
  }
}

export type TAlertRuleTestResponse = {
  matchingEntities: number
  previewAlerts: Array<{
    entityName: string
    entityType: string
    severity: string
    messageSubject: string
  }>
}

export type TAlertRuleSendTestResponse = {
  sentEmails: true
  createdAlerts: number
  alerts: Array<{
    _id: string
  }>
}

export type TAlertRuleTestOptions = {
  sendEmails?: boolean
}

export type TAlertPreviewEmail = {
  entityType: TTargetCollection
  entityId: string
  entityName: string
  entitySnapshot: Record<string, unknown>
  messageSubject: string
  messageBody: string
  lang: string
}

export type TAlertPreviewNotification = {
  id: string
  plannedAt: string
  daysFromNextRun: number
  target: {
    entityId: string
    entityType: TTargetCollection
    name: string
    firstname: string | null
    lastname: string | null
    picture: string | null
    subtitle: string | null
  }
  recipient: {
    type: 'resource' | 'team'
    id: string
    firstname?: string
    lastname?: string
    picture?: string | null
    display?: string
    email: string
    lang: string | null
  } | null
  email: TAlertPreviewEmail
}

export type TAlertPreviewResponse = {
  nextRunAt: string
  nextRunTargetCount: number
  horizonDays: number
  notifications: TAlertPreviewNotification[]
  isTruncated: boolean
  /** Daily application volume rule: today's count so far, in the offices' time zone. */
  volume?: {
    applicationsToday: number
    dateKey: string
    timeZone: string
    matches: boolean
    /** Over the threshold, but the scope was already alerted (cooldown, one email, snooze). */
    isHeld: boolean
  }
}

export type TAlertPreviewPayload = Pick<TAlertRulePayload, 'targetCollection' | 'conditions' | 'conditionLogic' | 'recipients' | 'officeIds' | 'jobCategoryIds' | 'excludeDepartureCategoryIds' | 'message' | 'severity' | 'cooldownHours' | 'notifyOnce' | 'sendOnWeekends' | 'sendOnPublicHolidays'> & {
  ruleId?: string
}

// API Functions
const filter = (axios: IFactoryFunctionRequest<any, any>) => (query: {
  page?: number
  limit?: number
  search?: string
  active?: boolean
  targetCollection?: TTargetCollection
  triggerType?: TTriggerType
}) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    query,
  })
}

const getById = (axios: IFactoryFunctionRequest<any, any>) => (id: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}`,
  })
}

const create = (axios: IFactoryFunctionRequest<any, any>) => (data: TAlertRulePayload) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    data,
  })
}

const update = (axios: IFactoryFunctionRequest<any, any>) => (id: string, data: Partial<TAlertRulePayload>) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}`,
    data,
  })
}

const remove = (axios: IFactoryFunctionRequest<any, any>) => (id: string) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}`,
  })
}

const toggle = (axios: IFactoryFunctionRequest<any, any>) => (id: string) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}/toggle`,
  })
}

/**
 * Test an alert rule with optional payload override
 * If payload is provided, it tests with the current form data (unsaved changes)
 * If no payload, it tests the saved rule
 */
const test = (axios: IFactoryFunctionRequest<any, any>) => (
  id: string,
  payload?: TAlertRulePayload,
  options: TAlertRuleTestOptions = {},
) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${id}/test`,
    data: payload,
    query: options.sendEmails ? { sendEmails: true } : undefined,
  })
}

const upcoming = (axios: IFactoryFunctionRequest<any, any>) => (query: { targetCollection?: TTargetCollection; ruleId?: string }) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/upcoming`,
    query,
  })
}

const preview = (axios: IFactoryFunctionRequest<any, any>) => (data: TAlertPreviewPayload) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/preview`,
    data,
  })
}

const previewEmail = (axios: IFactoryFunctionRequest<any, any>) => (data: { ruleName: string; severity: TSeverity; email: TAlertPreviewEmail; plannedAt: string }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/preview/email`,
    data,
  })
}

const runEngine = (axios: IFactoryFunctionRequest<any, any>) => () => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/run-engine`,
  })
}

export default (axios: AxiosInstance) => ({
  filter: filter(useAxios(axios)),
  getById: getById(useAxios(axios)),
  create: create(useAxios(axios)),
  update: update(useAxios(axios)),
  remove: remove(useAxios(axios)),
  toggle: toggle(useAxios(axios)),
  test: test(useAxios(axios)),
  upcoming: upcoming(useAxios(axios)),
  preview: preview(useAxios(axios)),
  previewEmail: previewEmail(useAxios(axios)),
  runEngine: runEngine(useAxios(axios)),
})
